import { inject, injectable } from 'inversify';

import { FormatService } from '@vk-hr-tek/core/format';
import { Calendar } from '@vk-hr-tek/core/calendar';

import { DateValidatorMessageContext, Validator } from '../../types';

@injectable()
export class EventsDateValidatorMessagesService {
  constructor(
    @inject(FormatService) private format: FormatService,
    @inject(Calendar) private calendar: Calendar,
  ) {}

  getPluralizedDaysCountText(count: number, workingDaysOnly: boolean) {
    return this.format.pluralize(Math.abs(count), [
      workingDaysOnly ? 'рабочий день' : 'календарный день',
      workingDaysOnly ? 'рабочих дня' : 'календарных дня',
      workingDaysOnly ? 'рабочих дней' : 'календарных дней',
    ]);
  }

  getMinDateValidatorMessage(
    validator: Validator,
    { attribute, tags, t }: DateValidatorMessageContext,
  ) {
    const { name } = attribute;
    if (validator.type === 'min_date') {
      const differenceDays = validator.days_count;
      const dayWord = this.getPluralizedDaysCountText(
        differenceDays,
        validator.working_days_only,
      );

      if (differenceDays > 0) {
        return t(
          [
            ...tags.map(
              (tag) => `event.eventDetail.validationRules.minDate.${tag}`,
            ),
            'event.eventDetail.validationRules.minDate.default',
          ],
          {
            name,
            dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays < -1) {
        return t(
          [
            ...tags.map(
              (tag) => `event.eventDetail.validationRules.minDateBefore.${tag}`,
            ),
            'event.eventDetail.validationRules.minDateBefore.default',
          ],
          {
            name,
            dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays === 0) {
        return t(
          [
            ...tags.map(
              (tag) => `event.eventDetail.validationRules.minDateEqual.${tag}`,
            ),
            'event.eventDetail.validationRules.minDateEqual.default',
          ],
          {
            name,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays === -1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.minDateNotBefore.${tag}`,
            ),
            'event.eventDetail.validationRules.minDateNotBefore.default',
          ],
          {
            name,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }
    }
  }

  getMaxDateValidatorMessage(
    validator: Validator,
    { attribute, tags, t }: DateValidatorMessageContext,
  ) {
    const { name } = attribute;
    if (validator.type === 'max_date') {
      const differenceDays = validator.days_count;
      const dayWord = this.getPluralizedDaysCountText(
        validator.days_count,
        validator.working_days_only,
      );

      if (differenceDays > 0) {
        return t(
          [
            ...tags.map(
              (tag) => `event.eventDetail.validationRules.maxDate.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDate.default',
          ],
          {
            name,
            dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays < 0) {
        return t(
          [
            ...tags.map(
              (tag) => `event.eventDetail.validationRules.maxDateBefore.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDateBefore.default',
          ],
          {
            name,
            dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays === 0) {
        return t(
          [
            ...tags.map(
              (tag) => `event.eventDetail.validationRules.maxDateEqual.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDateEqual.default',
          ],
          {
            name,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }
    }
  }

  getMinHiringDayDateValidatorMessage(
    validator: Validator,
    { tags, t }: DateValidatorMessageContext,
  ) {
    if (validator.type === 'min_hiring_day') {
      const messages = {
        ge: t([
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minHiringDayGE.${tag}`,
          ),
          'event.eventDetail.validationRules.minHiringDayGE.default',
        ]),
        gt: t([
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minHiringDayGT.${tag}`,
          ),
          'event.eventDetail.validationRules.minHiringDayGT.default',
        ]),
        le: t([
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minHiringDayLE.${tag}`,
          ),
          'event.eventDetail.validationRules.minHiringDayLE.default',
        ]),
        lt: t([
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minHiringDayLT.${tag}`,
          ),
          'event.eventDetail.validationRules.minHiringDayLT.default',
        ]),
        eq: t([
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minHiringDayEQ.${tag}`,
          ),
          'event.eventDetail.validationRules.minHiringDayEQ.default',
        ]),
        ne: t([
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minHiringDayNE.${tag}`,
          ),
          'event.eventDetail.validationRules.minHiringDayNE.default',
        ]),
      };

      return messages[validator.operation];
    }
  }

  getForbiddenPeriodsDateValidatorMessage(
    validator: Validator,
    { tags, t }: DateValidatorMessageContext,
  ) {
    if (
      validator.type === 'forbidden_periods' &&
      validator.existing_dates.length
    ) {
      const datesString = validator.existing_dates
        .map(({ from_date: from, to_date: to }) => {
          if (this.calendar.isSameDay(new Date(from), new Date(to))) {
            return this.format.toDate(from);
          } else {
            return `${this.format.toDate(from)} - ${this.format.toDate(to)}`;
          }
        })
        .join(', ');

      return t(
        [
          ...tags.map(
            (tag) =>
              `event.eventDetail.validationRules.forbiddenPeriods.${tag}`,
          ),
          'event.eventDetail.validationRules.forbiddenPeriods.default',
        ],
        {
          datesString,
        },
      );
    }
  }

  getMinWeekendDaysDateValidatorMessage(
    validator: Validator,
    { tags, t }: DateValidatorMessageContext,
  ) {
    if (validator.type === 'min_weekend_days') {
      return t(
        [
          ...tags.map(
            (tag) => `event.eventDetail.validationRules.minWeekendDays.${tag}`,
          ),
          'event.eventDetail.validationRules.minWeekendDays.default',
        ],
        {
          daysCount: this.format.pluralize(validator.working_days, [
            'день',
            'дня',
            'дней',
          ]),
          weekendsCount: this.format.pluralize(validator.min_weekend_days, [
            'выходного дня',
            'выходных дней',
            'выходных дней',
          ]),
        },
      );
    }
  }

  getMinDurationValidatorMessage(
    validator: Validator,
    { attribute, tags, t }: DateValidatorMessageContext,
  ) {
    const { name, nameValidating } = attribute;
    if (validator.type === 'min_duration') {
      const differenceDays = validator.days_count;
      const dayWord = this.getPluralizedDaysCountText(
        validator.days_count - 1,
        validator.working_days_only,
      );

      if (differenceDays > 1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.minDurationBefore.${tag}`,
            ),
            'event.eventDetail.validationRules.minDurationBefore.default',
          ],
          {
            nameValidating,
            name,
            daysCount: dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays < -1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.minDurationAfter.${tag}`,
            ),
            'event.eventDetail.validationRules.minDurationAfter.default',
          ],
          {
            nameValidating,
            name,
            daysCount: dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (validator.days_count === 0 || validator.days_count === 1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.minDurationEqual.${tag}`,
            ),
            'event.eventDetail.validationRules.minDurationEqual.default',
          ],
          {
            nameValidating,
            name,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (validator.days_count === -1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.minDurationEmpty.${tag}`,
            ),
            'event.eventDetail.validationRules.minDurationEmpty.default',
          ],
          {
            nameValidating,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }
    }
  }

  getMaxDurationValidatorMessage(
    validator: Validator,
    { attribute, tags, t }: DateValidatorMessageContext,
  ) {
    const { name, nameValidating } = attribute;
    if (validator.type === 'max_duration') {
      const differenceDays = validator.days_count;
      const dayWord = this.getPluralizedDaysCountText(
        validator.days_count - 1,
        validator.working_days_only,
      );
      if (differenceDays > 1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.maxDurationAfter.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDurationAfter.default',
          ],
          {
            nameValidating,
            name,
            daysCount: dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays < -1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.maxDurationBefore.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDurationBefore.default',
          ],
          {
            nameValidating,
            name,
            daysCount: dayWord,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays === 0 || differenceDays === 1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.maxDurationEqual.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDurationEqual.default',
          ],
          {
            nameValidating,
            name,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }

      if (differenceDays === -1) {
        return t(
          [
            ...tags.map(
              (tag) =>
                `event.eventDetail.validationRules.maxDurationEmpty.${tag}`,
            ),
            'event.eventDetail.validationRules.maxDurationEmpty.default',
          ],
          {
            nameValidating,
            interpolation: {
              escapeValue: false,
            },
          },
        );
      }
    }
  }
}
